import tw from 'twin.macro'
import { graphql } from 'gatsby'
import { clamp, motion } from 'framer-motion'
import { useIntersectionObserver } from 'usehooks-ts'
import { ContentfulRichTextGatsbyReference, RenderRichTextData } from 'gatsby-source-contentful/rich-text'

import Counter from '@/components/counter'
import RichText from '@/components/ui/rich-text'
import colors from '@/constants/colors'
import { useMemo } from 'react'
import ImageContentful from './ui/image-contentful'

const { primaryBlue, deepBlue, indigoBlue, lightGray, white } = colors

export const fragment = graphql`
  fragment BarGraphStat on ContentfulBarGraphStat {
    id: contentful_id
    label
    value
    averageValue
    logoIcon {
      ...Asset
    }
    value2
    averageValue2
    logoIcon2 {
      ...Asset
    }
    value3
    averageValue3
    logoIcon3 {
      ...Asset
    }
  }

  fragment SectionBarGraph on ContentfulSectionBarGraph {
    __typename
    contentful_id
    headline
    items: stats {
      ...BarGraphStat
    }
    bottomCaption {
      raw
      references {
        ...Link
        ...Asset
      }
    }
    backgroundColor
  }
`

export type AverageMarkerProps = {
  averageValue: number
  index: number
  isCompact?: boolean
}

export function AverageMarker({ averageValue, index, isCompact }: AverageMarkerProps) {
  return (
    <motion.div
      css={[
        tw`flex flex-col items-center absolute top-[calc(-50% - 14px)] -ml-12 md:(top-[calc(-50% + 2px)] -ml-8) z-10`,
        isCompact
          ? tw`top-[calc(-50% - 2px)] md:top-[calc(-50% + 10px)]`
          : tw`top-[calc(-50% - 14px)] md:top-[calc(-50% + 2px)]`,
      ]}
      style={{ left: `${averageValue}%` }}
      variants={{
        0: { opacity: 0, translateY: -30, translateX: '-50%' },
        100: { opacity: 1, translateY: 0, translateX: '-50%' },
      }}
      transition={{ duration: 0.4, ease: 'easeOut', delay: 1.5 + (index + 1) * 0.1 }}
    >
      <span css={[tw`whitespace-nowrap`, isCompact && tw`h-5 md:h-[22px]`]}>AVG: {averageValue}%</span>
      <div
        css={[tw`w-[3px] rounded-full bg-tealBlue`, isCompact ? tw`h-[48px] md:h-[80px]` : tw`h-[64px] md:h-[104px]`]}
      />
    </motion.div>
  )
}

export type BarGraphItemProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  id?: string
  value: number
  averageValue?: number
  logoIcon?: Queries.AssetFragment
  index: number
  isActive?: boolean
  color?: typeof indigoBlue | typeof primaryBlue | typeof lightGray
  backgroundColor?: typeof deepBlue | typeof lightGray | typeof white
  isCompact?: boolean
}

export function BarGraphItem({
  value,
  averageValue,
  index,
  logoIcon,
  isActive,
  color,
  backgroundColor,
  isCompact,
}: BarGraphItemProps) {
  return (
    <div tw="flex items-center">
      {logoIcon && (
        <div tw="w-8 h-8 md:(w-12 h-12) mr-4 md:mr-6">
          <ImageContentful {...logoIcon} />
        </div>
      )}
      <div tw="relative flex items-center  flex-1">
        {averageValue && <AverageMarker averageValue={averageValue} index={index} isCompact={isCompact} />}
        <div
          css={[
            tw`relative w-[calc(100% - 38px)] rounded-full overflow-hidden`,
            isCompact ? tw`h-[42px] md:h-[72px]` : tw`h-[48px] md:h-[78px]`,
            backgroundColor === deepBlue && tw`bg-deepBlue`,
            backgroundColor === lightGray && tw`bg-gray-100`,
            backgroundColor === white && tw`bg-white`,
          ]}
        >
          <motion.div
            style={{ width: `${value}%` }}
            tw="absolute top-0 left-0 h-full bg-gradient-to-r from-transparent to-100% origin-left rounded-br-full rounded-tr-full"
            css={[
              color === indigoBlue && tw`to-indigoBlue`,
              color === primaryBlue && tw`to-primaryBlue`,
              color === lightGray && tw`from-gray-300 to-gray-300`,
            ]}
            variants={{
              0: { translateX: '-100%' },
              100: { translateX: 0 },
            }}
            transition={{ duration: 1.5, ease: 'anticipate' }}
          />
        </div>
        <div tw="relative ml-4">
          <Counter
            tw="absolute top-0 bottom-0 left-0 right-0 m-auto w-full h-[40px] text-2xl"
            from={0}
            to={value}
            duration={1.5}
            postfix="%"
            isActive={isActive}
          />
        </div>
      </div>
    </div>
  )
}

export type BarGraphProps = {
  headline?: string
  items: Queries.BarGraphStatFragment[]
  bottomCaption?: RenderRichTextData<ContentfulRichTextGatsbyReference>
  backgroundColor?: typeof primaryBlue | typeof indigoBlue | typeof lightGray | typeof white
}

export default function BarGraph({ headline, items, bottomCaption, backgroundColor = primaryBlue }: BarGraphProps) {
  // scale the intersection threshold based on the number of items
  const threshold = clamp(0.01, 0.5, 0.5 - (items.length - 3) * 0.04)
  const { entry, ref } = useIntersectionObserver({ threshold, freezeOnceVisible: true })
  const isVisible = !!entry?.isIntersecting

  const barColors = useMemo(() => {
    switch (backgroundColor) {
      case primaryBlue:
        return [
          { color: indigoBlue, backgroundColor: deepBlue },
          { color: lightGray, backgroundColor: deepBlue },
          { color: lightGray, backgroundColor: deepBlue },
        ]
      case indigoBlue:
        return [
          { color: primaryBlue, backgroundColor: deepBlue },
          { color: lightGray, backgroundColor: deepBlue },
          { color: lightGray, backgroundColor: deepBlue },
        ]
      case lightGray:
        return [
          { color: primaryBlue, backgroundColor: white },
          { color: lightGray, backgroundColor: white },
          { color: lightGray, backgroundColor: white },
        ]
      case white:
        return [
          { color: primaryBlue, backgroundColor: lightGray },
          { color: lightGray, backgroundColor: lightGray },
          { color: lightGray, backgroundColor: lightGray },
        ]
      default:
        return [
          { color: primaryBlue, backgroundColor: white },
          { color: lightGray, backgroundColor: white },
          { color: lightGray, backgroundColor: white },
        ]
    }
  }, [backgroundColor])

  return (
    <div
      ref={ref}
      tw="rounded-3xl  bg-gradient-to-b  @xs:(w-full)"
      css={[
        !headline && tw`pt-8 lg:pt-16`,
        backgroundColor === primaryBlue && tw`bg-primaryBlue text-white`,
        backgroundColor === indigoBlue && tw`bg-indigoBlue text-white`,
        backgroundColor === lightGray && tw`bg-gray-100 text-indigoBlue`,
        backgroundColor === white && tw`bg-white text-indigoBlue`,
      ]}
    >
      {headline && (
        <h2 tw="text-3xl text-center pt-12 px-4 mb-8 @xs:(mb-4! text-3xl!) md:(text-[2.5rem] mb-16)">{headline}</h2>
      )}
      <motion.div
        tw="flex flex-col gap-8 p-[0px 60px 77px 40px] @xs:(pl-8! pr-14! py-4!) lg:(gap-12 p-[0px 97px 77px 121px])"
        transition={{ staggerChildren: 0.1 }}
        initial="0"
        animate={isVisible ? '100' : '0'}
      >
        {items?.map((item, index) => (
          <div key={item.id || item.label}>
            <h4 css={[tw`mb-9 md:mb-4 @xs:(text-xl) font-normal`, item.logoIcon && tw`ml-8 md:ml-[72px]`]}>
              {item.label}
            </h4>
            <div css={[tw`flex flex-col`, item.averageValue2 || item.averageValue3 ? tw`gap-y-8` : tw`gap-y-6`]}>
              <BarGraphItem
                index={index}
                value={item.value}
                averageValue={item.averageValue}
                logoIcon={item.logoIcon}
                isActive={isVisible}
                color={barColors[0].color}
                backgroundColor={barColors[0].backgroundColor}
                isCompact={!!(item.value2 || item.value3)}
              />
              {item.value2 && (
                <BarGraphItem
                  index={index}
                  value={item.value2}
                  averageValue={item.averageValue2}
                  logoIcon={item.logoIcon2}
                  isActive={isVisible}
                  color={barColors[1].color}
                  backgroundColor={barColors[1].backgroundColor}
                  isCompact
                />
              )}
              {item.value3 && (
                <BarGraphItem
                  index={index}
                  value={item.value3}
                  averageValue={item.averageValue3}
                  logoIcon={item.logoIcon3}
                  isActive={isVisible}
                  color={barColors[2].color}
                  backgroundColor={barColors[2].backgroundColor}
                  isCompact
                />
              )}
            </div>
          </div>
        ))}
      </motion.div>

      {bottomCaption?.raw && (
        <div tw="text-center pb-4 [a]:(text-tealBlue)">
          <RichText body={bottomCaption} />
        </div>
      )}
    </div>
  )
}
